import { ChartData, ChartOptions, ScriptableContext, TooltipItem } from "chart.js"
import { tailwindPalette } from "../../../../../theme/tailwindPalette"
import { WeekDayOccupancyRate } from "../../statisticsTypes"
import { AppLocalization } from "../../../../../localization/appLocalization"

type LoadLevel = "low" | "medium" | "high" | "peak"

export const loadLevels: LoadLevel[] = ["low", "medium", "high", "peak"]
export interface MatrixPoint {
    x: string
    y: string
    v: number
    d?: string
}
export const loadColors: Record<LoadLevel, string> = {
    low: tailwindPalette.orange[200],
    medium: tailwindPalette.orange[300],
    high: tailwindPalette.orange[400],
    peak: tailwindPalette.orange[500],
}

export const chartPlugins = {
    legend: {
        display: true,
        labels: {
            generateLabels() {
                return loadLevels.map((level, i) => ({
                    text: level.charAt(0).toUpperCase() + level.slice(1), // Capitalized
                    fillStyle: loadColors[level],
                    strokeStyle: loadColors[level],
                    lineWidth: 1,
                    hidden: false,
                    index: i,
                }))
            },
        },
    },
    tooltip: {
        callbacks: {
            title: () => "",
            label: (ctx: TooltipItem<"matrix">) => {
                const v = ctx.raw as MatrixPoint
                return getLoadLevel(v.v)
            },
        },
    },
}

export const getLoadLevel = (rate: number): LoadLevel => {
    if (rate >= 90) return "peak"
    if (rate >= 70) return "high"
    if (rate >= 40) return "medium"
    return "low"
}

export const getBgColor = (value: number) => loadColors[getLoadLevel(value)]

export const generateMatrixData = (weekDayOccupancyRates: WeekDayOccupancyRate[] = []) => {
    const xSet = new Set<string>() // weekday labels
    const ySet = new Set<string>() // time range labels
    const valueMap = new Map() // key: `${x}-${y}`

    for (const day of weekDayOccupancyRates) {
        const x = day.displayWeekDay
        xSet.add(x)

        for (const slot of day.hourlyOccupancyRates) {
            const y = `${slot.timeSlot.start.slice(0, 5)} - ${slot.timeSlot.end.slice(0, 5)}`
            ySet.add(y)
            valueMap.set(`${x}-${y}`, slot.occupancyRate)
        }
    }

    const xLabels = Array.from(xSet)
    const yLabels = Array.from(ySet).sort() // sorted by time

    const data = []

    for (const x of xLabels) {
        for (const y of yLabels) {
            data.push({
                x,
                y,
                v: valueMap.get(`${x}-${y}`) ?? 0,
                d: x,
            })
        }
    }

    return {
        data,
        xLabels,
        yLabels,
    }
}

export const getChartOptions = (xLabels: string[], yLabels: string[]) => {
    const options: ChartOptions = {
        scales: {
            x: {
                type: "category",
                labels: xLabels,
                position: "bottom",
                offset: true,
                ticks: {
                    font: { size: 9 },
                },
                grid: { display: false },
            },
            y: {
                type: "category",
                labels: yLabels,
                position: "left",
                offset: true,
                reverse: false,
                ticks: {
                    font: { size: 9 },
                },
                grid: { display: false },
            },
        },
        plugins: chartPlugins,
    }

    return options
}

export const getChartData = (data: MatrixPoint[], l10n: AppLocalization) => {
    const matrixChartData: ChartData<"matrix", MatrixPoint[]> = {
        datasets: [
            {
                label: l10n.getString("service-center-reports-statistics-peak-hours-heat-map-title"),
                data,
                backgroundColor({ raw }: ScriptableContext<"matrix">) {
                    return getBgColor((raw as { v: number })?.v) // as raw is unknown
                },
                borderWidth: 0,
                width: ({ chart }: ScriptableContext<"matrix">) =>
                    (chart.chartArea || {}).width / chart.scales["x"].ticks.length - 3,
                height: ({ chart }: ScriptableContext<"matrix">) =>
                    (chart.chartArea || {}).height / chart.scales["y"].ticks.length - 3,
            },
        ],
    }

    return matrixChartData
}
